import { useSyncExternalStore } from "react";

import {
  navPermissionsCacheKey,
  readCachedNavAccess,
  type NavAccessSnapshot,
} from "./nav-permissions-cache";

const navPermissionsCacheChanged = "tk-master:nav-permissions-cache-changed";

let cachedRaw: string | null | undefined;
let cachedUserId: string | undefined;
let cachedWorkspaceId: string | null | undefined;
let cachedSnapshot: NavAccessSnapshot | undefined;

function getStaleNavAccessSnapshot(
  userId: string | undefined,
  workspaceId: string | null,
): NavAccessSnapshot | undefined {
  if (typeof window === "undefined" || !userId || !workspaceId) return undefined;
  try {
    const raw = window.localStorage.getItem(navPermissionsCacheKey);
    if (
      raw === cachedRaw &&
      cachedUserId === userId &&
      cachedWorkspaceId === workspaceId
    ) {
      return cachedSnapshot;
    }
    cachedRaw = raw;
    cachedUserId = userId;
    cachedWorkspaceId = workspaceId;
    cachedSnapshot = readCachedNavAccess(userId, workspaceId) ?? undefined;
    return cachedSnapshot;
  } catch {
    return undefined;
  }
}

function subscribeStaleNavPermissions(onStoreChange: () => void): () => void {
  const handleChange = () => {
    cachedRaw = undefined;
    onStoreChange();
  };
  window.addEventListener(navPermissionsCacheChanged, handleChange);
  window.addEventListener("storage", handleChange);
  return () => {
    window.removeEventListener(navPermissionsCacheChanged, handleChange);
    window.removeEventListener("storage", handleChange);
  };
}

/** 通知同 tab 内权限缓存已更新（localStorage 不触发 storage 事件）。 */
export function notifyNavPermissionsCacheChanged(): void {
  if (typeof window === "undefined") return;
  cachedRaw = undefined;
  window.dispatchEvent(new Event(navPermissionsCacheChanged));
}

/** 客户端同步读取当前用户、当前工作空间的侧栏快照；SSR 返回 undefined。 */
export function useStaleNavAccess(
  userId: string | undefined,
  workspaceId: string | null,
): NavAccessSnapshot | undefined {
  return useSyncExternalStore(
    subscribeStaleNavPermissions,
    () => getStaleNavAccessSnapshot(userId, workspaceId),
    () => undefined,
  );
}
